import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { DownloadK8sAgentData, DownloadK8sEvents } from '../types/download-k8s-agent-data';
import { htmlEncode } from '../../../utils/escape.utils';
import { API_TYPE_COMPANIES_FOR_ACCOUNTS, WEB_COMPONENT_GZ_COPY_TO_CLIPBOARD } from '../../../webcomponents.constants';
import { translateFn } from '../../../utils/translator.helper';
import { LinkType } from 'apps/webcomponents/src/ui/gzLinkComponent/types';
import {
  HELM_LINK,
  ADD_HELM_CHART_TO_CLUSTER_SCRIPT,
  KUBECTL_LINK,
  DOWNLOAD_HELM_CHART_SCRIPT,
  KB_LINK,
} from '../constants';
import { RequiredValidator } from 'apps/webcomponents/src/ui/gzForm/validators/required.validator';
import { toggleVisibility } from 'apps/webcomponents/src/utils/htmlElement.utils';
import { ValidationResult } from 'apps/webcomponents/src/ui/gzForm/formField/types/form-validator';
import { CustomizableValidator } from 'apps/webcomponents/src/ui/gzForm/validators/customizable.validator';
import { ErrorDialogStrings } from 'apps/webcomponents/src/types';
import { GzDialogWebComponent } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialogWebComponent';
import { GzDialog } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialog';

export class DownloadK8sAgentView extends BaseWebComponentView {
  private companyIdsWithKspm: string[] = [];
  public isSelectedCompanyValid: boolean = true;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),

      $downloadInstructions: this.shadow.querySelector('#downloadInstructions'),

      $configurationTitle: this.shadow.querySelector('#configurationTitle'),
      $companyFormField: this.shadow.querySelector('#companyFormField'),

      $checkRequirementsTitle: this.shadow.querySelector('#checkRequirementsTitle'),
      $checkStep1: this.shadow.querySelector('#checkStep1'),
      $checkStep2: this.shadow.querySelector('#checkStep2'),
      $checkStep3: this.shadow.querySelector('#checkStep3'),
      $checkStep4: this.shadow.querySelector('#checkStep4'),

      $k8sScriptTitle: this.shadow.querySelector('#k8sScriptTitle'),
      $connectStep1: this.shadow.querySelector('#connectStep1'),
      $downloadHelmChartScript: this.shadow.querySelector('#downloadHelmChartScript'),
      $connectStep2: this.shadow.querySelector('#connectStep2'),
      $kubernetesDownloadK8sAgentScript: this.shadow.querySelector('#kubernetesDownloadK8sAgentScript'),
      $kubernetesDownloadK8sAgentScriptInput: this.shadow.querySelector('#kubernetesDownloadK8sAgentScriptInput'),
      $k8sScriptFooter: this.shadow.querySelector('#k8sScriptFooter'),

      $verificationTitle: this.shadow.querySelector('#verificationTitle'),
      $verificationInstructions: this.shadow.querySelector('#verificationInstructions'),

      $copyToClipboardItems: this.shadow.querySelectorAll(WEB_COMPONENT_GZ_COPY_TO_CLIPBOARD),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = {
      errorDialogTitle: translateFn('Unable to load the page'),
      errorDialogMessage: translateFn('Something went wrong while processing your request. Please contact support.'),
    };
  }

  setTranslatedStrings() {
    this.dom.$copyToClipboardItems.forEach(el => {
      el.confirmationMessage = this.commonStrings.copied;
    });

    this.dom.$companyFormField.label = this.dependencies.translateFn('Company');
    if (this.dom.$companyFormField.Input) {
      this.dom.$companyFormField.Input.placeholder = this.dependencies.translateFn('Choose company');
      this.dom.$companyFormField.Input.searchPlaceholderString = this.dependencies.translateFn('Search');
      this.dom.$companyFormField.Input.noSearchResultsString = this.dependencies.translateFn('No options found');
    }
  }

  private setCompanyValidators(): void {
    this.dom.$companyFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      CustomizableValidator(this.KspmLicenseValidatorFunction.bind(this)),
    ];
  }

  private KspmLicenseValidatorFunction(value: string): ValidationResult {
    if (!this.companyIdsWithKspm.includes(value)) {
      this.dom.$companyFormField.dispatchEvent(
        new CustomEvent(DownloadK8sEvents.CompanyDropdownValidated, {
          bubbles: true,
          composed: true,
          detail: {
            selectedValue: value,
            valid: false,
          },
        })
      );

      return {
        valid: false,
        error: translateFn('This company does not have eligible license for this feature.'),
      };
    }

    // dispatch event to notify that the selected company is valid
    this.dom.$companyFormField.dispatchEvent(
      new CustomEvent(DownloadK8sEvents.CompanyDropdownValidated, {
        bubbles: true,
        composed: true,
        detail: {
          selectedValue: value,
          valid: true,
        },
      })
    );
    return { valid: true };
  }

  private setCompanyDropdown(data: DownloadK8sAgentData): void {
    if (this.dependencies.permissions.canHaveChildCompanies) {
      toggleVisibility(this.dom.$configurationTitle, true);
      toggleVisibility(this.dom.$companyFormField, true);
      this.dom.$companyFormField.removeAttribute('disabled');

      // reset selected value
      this.dom.$companyFormField.Input.setFormValue('');
      this.dom.$companyFormField.resetField();

      this.dom.$companyFormField.Input.setApiType(API_TYPE_COMPANIES_FOR_ACCOUNTS);
      this.dom.$companyFormField.Input.updateComplete.then(() => {
        this.dom.$companyFormField.Input.controller.setFilterOptions().catch();
      });
    } else {
      toggleVisibility(this.dom.$configurationTitle, false);
      toggleVisibility(this.dom.$companyFormField, false);
      this.dom.$companyFormField.setAttribute('disabled', '');
    }
  }

  // dispatch an event to notify that the page has been opened anew
  // this is used to reset the companyId in the controller
  private dispatchPageOpenedEvent(): void {
    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(DownloadK8sEvents.DownloadK8sPageOpened, {
        bubbles: true,
        composed: true,
      })
    );
  }

  protected buildDialogElement(strings: ErrorDialogStrings): GzDialogWebComponent {
    const { title, message, actionButtonString } = strings;

    const dialogBody = document.createElement('div');
    // dialogBody.style.width = '100%';
    dialogBody.innerHTML = `
      <gz-dialog-notification-content
      iconName="ds-icon-notification_negative_XL"
      iconColor="var(--red-layout-300-primary)">
        <gz-text slot="message">${message}</gz-text>
      </gz-dialog-notification-content>
    `;

    const dialogFooter = document.createElement('div');
    dialogFooter.style.justifyContent = 'flex-end'; // align the Cancel button to the right
    dialogFooter.innerHTML = `<gz-button type='outline' color='secondary'>${actionButtonString}</gz-button>`;

    const actionButton = dialogFooter.querySelector('gz-button');

    const dialogConfigs: GzDialog = {
      headline: title,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

    if (actionButton) {
      actionButton.addEventListener('click', () => dialog.close());
    }

    document.body.appendChild(dialog);
    dialog.open();

    return dialog;
  }

  buildErrorView() {
    // disconnect turns off validation of the dropdown after error dialog
    // this.disconnected();

    const dialog = this.buildDialogElement({
      title: this.translations.errorDialogTitle,
      message: this.translations.errorDialogMessage,
      actionButtonString: this.commonStrings.close,
    });

    const closeButton = dialog.footerContent?.querySelector('gz-button');
    if (closeButton) {
      closeButton.setAttribute('color', 'secondary');
    }
  }

  buildSuccessView(data: DownloadK8sAgentData) {
    this.dispatchPageOpenedEvent();
    this.companyIdsWithKspm = data.companyIdsWithKspm || [];
    this.setCompanyDropdown(data);
    this.setCompanyValidators();

    // TODO: Temporarily hide these parts. Enable these when GA.
    toggleVisibility(this.dom.$k8sScriptFooter, false);
    toggleVisibility(this.dom.$verificationTitle, false);
    toggleVisibility(this.dom.$verificationInstructions, false);

    this.dom.$downloadInstructions.innerHTML = translateFn(
      'To set up a Kubernetes cluster, follow these steps and utilize the provided scripts within your cluster management platform.'
    );

    this.dom.$configurationTitle.innerHTML = translateFn('Configuration');

    this.buildCheckRequirementsSection();

    this.dom.$k8sScriptTitle.innerHTML = translateFn('Connect to your Kubernetes cluster');
    this.dom.$connectStep1.innerHTML = translateFn('Download the Helm chart package provided below.');
    this.dom.$connectStep2.innerHTML = translateFn('Add the Helm chart to your cluster.');
    this.dom.$downloadHelmChartScript.setAttribute('value', DOWNLOAD_HELM_CHART_SCRIPT);
    this.dom.$downloadHelmChartScript.innerHTML = DOWNLOAD_HELM_CHART_SCRIPT;
    this.dom.$k8sScriptFooter.innerHTML = `${htmlEncode(
      this.dependencies.translateFn('For a complete Kubernetes Agent guide, refer to')
    )} <gz-link type="${LinkType.UNDERLINE}" id='gravityZoneDownloadK8sKbUrl' href=${KB_LINK}><span>${htmlEncode(
      this.dependencies.translateFn('this KB article')
    )}.</span></gz-link>`;

    this.dom.$verificationTitle.innerHTML = translateFn('View onboarded clusters on Networks page');
    this.dom.$verificationInstructions.innerHTML = translateFn(
      'Once the script has finished running and the Kubernetes agent is successfully installed, you can view the onboarded cluster on the Network page. Please note that this process may take a few minutes.'
    );

    /**
     * If the user is from a Customer company, it will generate a script based on the current user's company data.
     * If the user is from a Partner company, it will prompt them to select a company.
     */
    if (Application.Main.company.isCustomer()) {
      // for Customer companies, use data for the current user's company and generate the script
      this.setK8sScriptInput(Application.Main.getUserCompany()?.id || '', data);
    } else {
      // for Partner companies, set the placeholder text in the script
      this.setK8sScriptPlaceholder();
    }
  }

  buildCheckRequirementsSection(): void {
    this.dom.$checkRequirementsTitle.innerHTML = translateFn('Check requirements');
    this.dom.$checkStep1.innerText = this.dependencies.translateFn('Ensure you have named your cluster.');
    this.dom.$checkStep2.innerText = this.dependencies.translateFn(
      'Ensure you have an account with installation permissions for your cluster, capable of creating deployments, CronJobs, ConfigMaps, and secrets.'
    );
    this.dom.$checkStep3.innerHTML = `
      ${this.dependencies.translateFn('Install')}
      <gz-link type="${LinkType.UNDERLINE}" href=${HELM_LINK}><span>${htmlEncode('Helm')}</span></gz-link>
      ${this.dependencies.translateFn('and')}
      <gz-link type="${LinkType.UNDERLINE}" href=${KUBECTL_LINK}><span>${htmlEncode('Kubectl')}</span></gz-link>.`;
    this.dom.$checkStep4.innerText = this.dependencies.translateFn(
      'The Helm chart is optimized for small to medium-sized clusters, needing at least 2 GiB of RAM and 2 CPUs. Adjust the resource usage of the cluster accordingly if it is larger.'
    );
  }

  /**
   * Sets the Kubernetes script placeholder in the input field upon page load.
   */
  public setK8sScriptPlaceholder(): void {
    // for Partner companies, prompt the user to select a company
    const initialScriptText = translateFn('Choose a company from the company selector to generate the code.');
    this.dom.$kubernetesDownloadK8sAgentScriptInput.setAttribute('value', initialScriptText);
    this.dom.$kubernetesDownloadK8sAgentScriptInput.innerHTML = initialScriptText;

    // set the style of the placeholder text
    this.dom.$kubernetesDownloadK8sAgentScriptInput.style.fontStyle = 'italic';
    this.dom.$kubernetesDownloadK8sAgentScriptInput.style.color = this.getColorFromStyles('--chart-300-icon-buttons');
  }

  /**
   * Set the actual Kubernetes script for the specific company.
   * @param companyId The id of the company for which the script is generated.
   * @param data Contains the data required to generate the script.
   */
  setK8sScriptInput(companyId: string, data: DownloadK8sAgentData): void {
    // reset the style of the text
    this.dom.$kubernetesDownloadK8sAgentScriptInput.style.fontStyle = 'normal';
    this.dom.$kubernetesDownloadK8sAgentScriptInput.style.color = this.getColorFromStyles('--body-text-arrows-400');

    // set the script mapping
    const scriptKeyValueMapping = {
      GZ_SERVER: data.gzServer,
      CNAPP_SERVER: data.cnappServer,
      COMPANY_ID: companyId,
      CSR_TOKEN: data.csrToken,
      ACCESS_KEY: data.accessToken,
    };

    // replace placeholders with actual values
    const k8sScript = ADD_HELM_CHART_TO_CLUSTER_SCRIPT.replace(
      /\<(.*?)\>/g,
      (_, key) => scriptKeyValueMapping[key] || ''
    );

    this.dom.$kubernetesDownloadK8sAgentScriptInput.innerHTML = k8sScript;
    this.dom.$kubernetesDownloadK8sAgentScriptInput.setAttribute('value', k8sScript);
  }

  private getColorFromStyles(colorName: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(colorName).trim();
  }
}
